import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { equals, findIndex, forEach, is, isNil, length, prop, propEq } from 'ramda';
import { onUploadFile } from './onUploadFile.js';

const useControlledFileUpload = ({
  control,
  name,
  getMiddleText,
  uploadedFiles,
  setError,
  deleteMutation,
  maxFileSize,
  uploadedFileLimit,
  projectId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fileToRemove, setFileToRemove] = useState(null);

  const {
    append,
    fields,
    remove
  } = useFieldArray({
    control,
    name
  });

  const getUploadFileErrorMessage = (code) => {
    switch (code) {
      case 'LIMIT_FILE_SIZE':
        return getMiddleText('FILE_SIZE_ERROR_MESSAGE', { fileSizeLimit: maxFileSize });
      case 'UNACCEPTED_FILETYPE':
        return getMiddleText('FILE_TYPE_ERROR_MESSAGE');
      default:
        return null;
    }
  };

  const onChange = async ({
    target: {
      validity,
      files
    },
    fileType
  }) => {
    setIsLoading(true);

    if (length(fields) + length(files) > uploadedFileLimit) {
      setIsLoading(false);
      return;
    }

    if (validity.valid) {
      const formData = new FormData();
      const newFiles = [];
      forEach((file) => {
        const fileName = prop('name', file);
        if (equals(-1, findIndex(propEq('fileName', fileName), uploadedFiles))) {
          formData.append('files', file);
          formData.append('fileType', fileType);
          formData.append('fileName', fileName);
          formData.append('externalId', projectId);
          formData.append('projectId', projectId);
          newFiles.push(file);
        }
      })(files);

      const response = await onUploadFile(uploadedFiles, fileType, name, formData, append);

      if (!response.ok || isNil(response.data)) {
        console.error('[FileUpload] Error from upload:', response.error);
        setError(name, {
          type: 'custom',
          message: getUploadFileErrorMessage(response.error)
        });
      } else {
        setError(name, null);
      }
      setIsLoading(false);
    }
  };

  const onRemove = async () => {
    if (!fileToRemove || !is(Function, deleteMutation)) return;
    const { index, uploadId } = fileToRemove;
    await deleteMutation({ uploadId }, (ok, err) => {
      if (ok && !err) {
        remove(index);
        setFileToRemove(null);
      } else {
        console.log('[FileUpload] Error removing file:', err);
      }
    });
  };

  return {
    remove,
    onChange,
    isLoading,
    fileToRemove,
    setFileToRemove,
    fields,
    onRemove
  };
};

export default useControlledFileUpload;
